import { z } from "zod";
import { isBlockedTestLabel } from "../../shared-utils/productionDataGuard.mjs";

/** Shown inline on forms and in submit toasts when a test/demo asset name is rejected. */
export const PRODUCTION_TEST_LABEL_MESSAGE =
  'Please use a real asset name. Test or demo labels (e.g. "Gold for test", "sample data") aren\'t allowed.';

const USER_FACING_ASSET_ERROR_CODES = new Set([
  "PROD_DATA_GUARD",
  "INVALID_ASSET_NAME",
  "DUPLICATE_BANK_BALANCE",
]);

/**
 * Returns a user-facing error when the asset name matches production test/demo blocklist.
 * Only enforced in production builds to mirror backend behavior.
 */
export function getProductionAssetNameError(name: string): string | undefined {
  if (!import.meta.env.PROD) {
    return undefined;
  }

  const normalized = name.trim();
  if (!normalized) {
    return undefined;
  }

  if (isBlockedTestLabel(normalized)) {
    return PRODUCTION_TEST_LABEL_MESSAGE;
  }

  return undefined;
}

/**
 * Add Production Asset Name Issue.
 */
export function addProductionAssetNameIssue(
  ctx: z.RefinementCtx,
  assetName: string,
  path: [string, ...string[]]
): void {
  const message = getProductionAssetNameError(assetName);
  if (message) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path,
    });
  }
}

/**
 * Is User Facing Asset Service Error.
 */
export function isUserFacingAssetServiceError(error: unknown): boolean {
  if (typeof error === "object" && error !== null && "userFacing" in error) {
    return (error as { userFacing?: boolean }).userFacing === true;
  }

  if (typeof error === "object" && error !== null && "code" in error) {
    const { code } = error as { code?: unknown };
    if (typeof code === "string" && USER_FACING_ASSET_ERROR_CODES.has(code)) {
      return true;
    }
  }

  const message = error instanceof Error ? error.message : "";
  return (
    message.includes("Test or demo labels are not allowed") ||
    message.includes(PRODUCTION_TEST_LABEL_MESSAGE) ||
    message.includes("Duplicate bank account") ||
    message.includes("Another asset already uses this bank")
  );
}

/**
 * Get User Facing Asset Error Title.
 */
export function getUserFacingAssetErrorTitle(error: unknown, defaultTitle: string): string {
  if (!isUserFacingAssetServiceError(error)) {
    return defaultTitle;
  }

  const message = error instanceof Error ? error.message : "";
  if (message.includes("Duplicate") || message.includes("duplicate")) {
    return "Duplicate bank account";
  }

  return "Invalid asset name";
}

/** User-facing copy when encryption key provisioning fails (BUG-03). */
export const ENCRYPTION_KEY_USER_MESSAGE =
  "Unable to secure this data. Please sign out and sign back in, then try again.";

/**
 * Resolve User Facing Asset Error Message.
 */
function resolveUserFacingAssetErrorMessage(message: string): string | undefined {
  if (
    message.includes("encryption key") ||
    message.includes("Encryption key") ||
    message.includes("Failed to get or create encryption key")
  ) {
    return ENCRYPTION_KEY_USER_MESSAGE;
  }

  if (
    message.includes("Test or demo labels are not allowed") ||
    message === PRODUCTION_TEST_LABEL_MESSAGE
  ) {
    return PRODUCTION_TEST_LABEL_MESSAGE;
  }

  if (message.includes("Asset service error (")) {
    const wrappedMessagePattern = /Asset service error \([^)]+\): (.+)/;
    const match = wrappedMessagePattern.exec(message);
    const inner = match?.[1];
    if (inner?.includes("Test or demo labels")) {
      return PRODUCTION_TEST_LABEL_MESSAGE;
    }
    if (inner) {
      return inner;
    }
    return undefined;
  }

  return message;
}

/**
 * Get User Facing Asset Error Description.
 */
export function getUserFacingAssetErrorDescription(
  error: unknown,
  fallback: string,
  formatSubmitError?: (error: unknown) => string
): string {
  if (isUserFacingAssetServiceError(error) && error instanceof Error) {
    const resolved = resolveUserFacingAssetErrorMessage(error.message);
    if (resolved) {
      return resolved;
    }
  }

  if (formatSubmitError && !isUserFacingAssetServiceError(error)) {
    return formatSubmitError(error);
  }

  if (error instanceof Error) {
    const resolved = resolveUserFacingAssetErrorMessage(error.message);
    if (resolved) {
      return resolved;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}
